import styled from '@emotion/styled';
import { addDays } from 'date-fns/addDays';
import { borderRadii } from 'app/spacing';
import MonthCalendarCellHeader from './MonthCalendarCellHeader';
import { isCellToday } from '../utils';

// 1. apply top and left border to cell
// 2. apply bottom border to last row & today
// 3. apply right border to last column & today
// 4. remove bottom top border from cell below today
// 5. remove left border from cell to the right of today
const Container = styled.div`
  position: relative;
  border: 1px solid;
  border-color: ${({ isToday }) =>
    isToday
      ? 'var(--color-button-secondary-default)'
      : 'var(--color-button-disabled-default)'};

  border-bottom-width: ${({ index, total, isToday, numColumns = 7 }) =>
    isToday || index >= total - numColumns ? '1px' : 0};
  border-right-width: ${({ index, isToday, numColumns = 7 }) =>
    isToday || (index + 1) % numColumns === 0 ? '1px' : 0};
  border-top-width: ${({ removeTopBorder }) => (removeTopBorder ? 0 : '1px')};
  border-left-width: ${({ removeLeftBorder }) =>
    removeLeftBorder ? 0 : '1px'};

  border-top-left-radius: ${({ index }) =>
    index === 0 ? borderRadii.standard : 0};
  border-top-right-radius: ${({ index, numColumns = 7 }) =>
    index < numColumns && (index + 1) % numColumns === 0
      ? borderRadii.standard
      : 0};
  border-bottom-left-radius: ${({ index, total, numColumns = 7 }) =>
    index >= total - numColumns && index % numColumns === 0
      ? borderRadii.standard
      : 0};
  border-bottom-right-radius: ${({ index, total }) =>
    index === total - 1 ? borderRadii.standard : 0};

  background-color: ${({ isToday, isCurrentMonth }) => {
    if (isToday) {
      return 'var(--color-background-hover)';
    }
    return isCurrentMonth
      ? 'var(--color-background-white)'
      : 'var(--color-background-lightest)';
  }};

  overflow: hidden;
`;

const MonthCalendarCell = ({
  date,
  calendarMonth,
  calendarYear,
  index,
  total,
  numColumns,
  children,
  ...rest
}) => {
  const today = new Date();
  const yesterday = addDays(today, -1);
  const lastWeek = addDays(today, -7);
  const isToday = isCellToday(date);
  const isCurrentMonth = calendarMonth === date.getMonth();
  const removeTopBorder =
    lastWeek.getFullYear() === calendarYear &&
    lastWeek.getMonth() === calendarMonth &&
    lastWeek.getDate() === today.getDate();
  const removeLeftBorder =
    date.getFullYear() === calendarYear &&
    date.getMonth() === calendarMonth &&
    yesterday.getDate() === today.getDate();

  return (
    <Container
      index={index}
      total={total}
      numColumns={numColumns}
      isToday={isToday}
      isCurrentMonth={isCurrentMonth}
      removeTopBorder={removeTopBorder}
      removeLeftBorder={removeLeftBorder}
      {...rest}
    >
      {children}
    </Container>
  );
};

export const DefaultMonthCalendarCell = ({
  date,
  calendarMonth,
  children,
  numColumns = 7,
  ...rest
}) => (
  <MonthCalendarCell
    date={date}
    calendarMonth={calendarMonth}
    numColumns={numColumns}
    {...rest}
  >
    <MonthCalendarCellHeader date={date} calendarMonth={calendarMonth} />
    {children}
  </MonthCalendarCell>
);

export default MonthCalendarCell;
